import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SplashScreen from './components/common/SplashScreen';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAppContext } from './AppContext';
import { useAuthContext } from './contexts/AuthContext';
import { useFormProgressContext } from './contexts/FormProgressContext';
import { AppChromeProvider } from './contexts/AppChromeContext';
import { useAppBootstrap } from './hooks/useAppBootstrap';
import { usePrefetch } from './hooks/usePrefetch';
import { createAppRouter } from './routes';
import lazyWithRetry from './utils/lazyWithRetry';
const AppShell = lazyWithRetry(() => import('./AppShell'));
import {
  STORAGE_KEYS,
  readStorageItem,
  removeStorageItem,
} from './utils/storage';
import { dataFlowLog, summarizeToken, summarizeUser } from './utils/dataFlowLogger';

import './App.less';

const enableSpeedInsights = import.meta.env.VITE_ENABLE_SPEED_INSIGHTS === 'true';

let speedInsightsClient;

if (typeof window !== 'undefined' && import.meta.env.PROD && enableSpeedInsights) {
  import('@vercel/speed-insights')
    .then(({ injectSpeedInsights }) => {
      try {
        speedInsightsClient = injectSpeedInsights({
          framework: 'react-router',
          route: window.location?.pathname ?? null,
        });
      } catch (error) {
        dataFlowLog('app.speed-insights.error', {
          message: error?.message || 'Unknown injection error',
        });
      }
    })
    .catch((error) => {
      dataFlowLog('app.speed-insights.error', {
        message: error?.message || 'Unknown injection error',
      });
    });
}

function App() {
  const { loading } = useAppContext();
  const { user } = useAuthContext();

  return (
    <ErrorBoundary>
      <Suspense fallback={<SplashScreen />}>
        <>
          <MainApp />
          {!user && loading ? <SplashScreen overlay /> : null}
        </>
      </Suspense>
    </ErrorBoundary>
  );
}

function MainApp() {
  const { setHomeHeroReady } = useAppContext();
  const {
    token,
    user,
    sessionState,
    shouldPromptReauth,
    setShouldPromptReauth,
    setUser,
  } = useAuthContext();
  const {
    listSavedForms,
    acknowledgeRecovery,
    formsRevision,
    triggerRecoveryPrompt,
    clearAllForms,
  } = useFormProgressContext();
  const userId = user?.id || user?._id || user?.userId || null;
  const isAuthenticated = Boolean(user && token);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [discoverPanel, setDiscoverPanel] = useState(0);
  const [administrationPanel, setAdministrationPanel] = useState(1);

  const recoveryActionsRef = useRef({
    resetRecoveryBannerShown: () => {},
    hideRecoveryBanner: () => {},
  });

  const handleSignIn = useCallback((nextUser) => {
    dataFlowLog('app.main.handleSignIn', {
      user: summarizeUser(nextUser),
    });
    recoveryActionsRef.current.resetRecoveryBannerShown();
    recoveryActionsRef.current.hideRecoveryBanner();
    setUser(nextUser);
    setHomeHeroReady(false);
  }, [setHomeHeroReady, setUser]);

  const closeNotificationsPanel = useCallback(() => {
    setShowNotificationsPanel(false);
  }, []);

  const toggleNotificationsPanel = useCallback(() => {
    setShowNotificationsPanel((prev) => !prev);
  }, []);

  useEffect(() => {
    dataFlowLog('app.main.auth-gate', {
      isAuthenticated,
      user: summarizeUser(user),
      token: summarizeToken(token),
    });
  }, [isAuthenticated, token, user]);

  const router = useMemo(() => createAppRouter({ isAuthenticated }), [isAuthenticated]);

  const appChromeValue = useMemo(() => ({
    showNotificationsPanel,
    onToggleNotifications: toggleNotificationsPanel,
    onCloseNotifications: closeNotificationsPanel,
    onSignIn: handleSignIn,
    discoverPanel,
    administrationPanel,
    setDiscoverPanel,
    setAdministrationPanel,
  }), [
    administrationPanel,
    closeNotificationsPanel,
    discoverPanel,
    handleSignIn,
    showNotificationsPanel,
    toggleNotificationsPanel,
  ]);

  usePrefetch({ user, token });

  const {
    recoveryForms,
    setRecoveryForms,
    showRecoveryBanner,
    hideRecoveryBanner,
    resetRecoveryBannerShown,
  } = useAppBootstrap({
    router,
    speedInsightsClient,
    showNotificationsPanel,
    setShowNotificationsPanel,
    listSavedForms,
    formsRevision,
    userId,
  });

  recoveryActionsRef.current.resetRecoveryBannerShown = resetRecoveryBannerShown;
  recoveryActionsRef.current.hideRecoveryBanner = hideRecoveryBanner;

  const handleSessionReauthSuccess = useCallback(() => {
    setShouldPromptReauth(false);
    resetRecoveryBannerShown();

    try {
      const resumeUrl = readStorageItem(STORAGE_KEYS.LAST_PROTECTED_ROUTE, { area: 'session' });
      if (resumeUrl) {
        router.navigate(resumeUrl);
        removeStorageItem(STORAGE_KEYS.LAST_PROTECTED_ROUTE, { area: 'session' });
      }
    } catch {
      // ignore
    }

    const saved = listSavedForms();
    if (saved.length > 0) {
      triggerRecoveryPrompt();
      setRecoveryForms(saved);
      return;
    }

    setRecoveryForms([]);
    acknowledgeRecovery();
    hideRecoveryBanner();
  }, [
    acknowledgeRecovery,
    hideRecoveryBanner,
    listSavedForms,
    resetRecoveryBannerShown,
    router,
    setRecoveryForms,
    setShouldPromptReauth,
    triggerRecoveryPrompt,
  ]);

  const handleResumeForm = useCallback((formId) => {
    const entry = listSavedForms().find((item) => item.id === formId);
    setRecoveryForms([]);
    acknowledgeRecovery();
    hideRecoveryBanner();
    resetRecoveryBannerShown();

    if (!entry) return;

    const path = entry.meta?.path || '/';
    const resumeAction = entry.meta?.resumeAction;
    router.navigate(path, {
      state: {
        resumeFormId: formId,
        ...(resumeAction ? { resumeAction } : {}),
      },
    });
  }, [
    acknowledgeRecovery,
    hideRecoveryBanner,
    listSavedForms,
    resetRecoveryBannerShown,
    router,
    setRecoveryForms,
  ]);

  const handleDismissRecovery = useCallback(() => {
    setRecoveryForms([]);
    acknowledgeRecovery();
    hideRecoveryBanner();
    resetRecoveryBannerShown();
  }, [acknowledgeRecovery, hideRecoveryBanner, resetRecoveryBannerShown, setRecoveryForms]);

  const handleClearForms = useCallback(() => {
    clearAllForms();
    setRecoveryForms([]);
    acknowledgeRecovery();
    hideRecoveryBanner();
    resetRecoveryBannerShown();
  }, [
    acknowledgeRecovery,
    clearAllForms,
    hideRecoveryBanner,
    resetRecoveryBannerShown,
    setRecoveryForms,
  ]);

  return (
    <AppChromeProvider value={appChromeValue}>
      <Suspense fallback={null}>
        <AppShell
          user={user}
          token={token}
          sessionState={sessionState}
          shouldPromptReauth={shouldPromptReauth}
          onAuthenticated={handleSessionReauthSuccess}
          showRecoveryBanner={showRecoveryBanner}
          recoveryForms={recoveryForms}
          onResume={handleResumeForm}
          onDismiss={handleDismissRecovery}
          onClear={handleClearForms}
          router={router}
        />
      </Suspense>
    </AppChromeProvider>
  );
}

export default App;
